/* Analytics, in the prototype's layout: a right-aligned filter row, two sub-tabs, one KPI
   strip, then three pairs of panels.

   The prototype's panels are all empty -- it has no backend -- and every dashed well below
   is the same well, shown for the same reason: there is nothing to rank yet. The
   difference is that here it is an answer rather than a placeholder. Where the API returns
   null (a rate over nothing, a delta with no prior period) that renders as an em dash
   rather than 0%, because the two mean different things and only one of them is true.

   Two of the prototype's panels have no endpoint behind them -- a per-repository merge
   time breakdown, and most-upvoted comments -- so they render the well. Its "Export"
   button is left out rather than wired to nothing. */

import { useState } from "react";

import { api, query } from "@/api";
import { useOrgPath } from "@/session";
import { useApi } from "@/useApi";
import {
  Async,
  Chevron,
  Empty,
  FilterPill,
  KPI,
  KPI_STRIP,
  LineChart,
  Panel,
  PanelPair,
  Segments,
  column,
  duration,
} from "@/ui";

const RANGES = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "This month" },
  { value: "90", label: "Last 90 days" },
  { value: "365", label: "Last year" },
];

const METRICS = [
  ["reviews", "PR reviews"],
  ["findings", "Bugs caught"],
];

const SUBTITLE = { padding: "8px 18px 0", font: "var(--text-body-14)", color: "var(--text-muted)" };

const BIG = {
  font: "500 clamp(24px,2.4vw,34px)/1 var(--font-display)",
  letterSpacing: "-0.03em",
  color: "var(--text-primary)",
};

export function Analytics() {
  const orgPath = useOrgPath();
  const [days, setDays] = useState("30");
  const [repositoryId, setRepositoryId] = useState(null);
  const [author, setAuthor] = useState(null);
  const [metric, setMetric] = useState("reviews");

  const filters = useApi(
    (signal) => api.get(`${orgPath}/analytics/filters`, { signal }),
    [orgPath],
    { skip: !orgPath },
  );

  const report = useApi(
    (signal) =>
      api.get(`${orgPath}/analytics${query({ days, repository_id: repositoryId, author })}`, {
        signal,
      }),
    [orgPath, days, repositoryId, author],
    { skip: !orgPath },
  );

  return (
    <>
      <div style={{ display: "flex", gap: "9px", flexWrap: "wrap", justifyContent: "flex-end" }}>
        <FilterPill
          label="All repositories"
          value={repositoryId}
          onChange={setRepositoryId}
          options={(filters.data?.repositories ?? []).map((item) => ({
            value: item.id,
            label: item.name,
          }))}
        />
        <FilterPill
          label="All authors"
          value={author}
          onChange={setAuthor}
          options={(filters.data?.authors ?? []).map((item) => ({ value: item, label: item }))}
        />
        <FilterPill
          label="This month"
          value={days === "30" ? null : days}
          onChange={(value) => setDays(value ?? "30")}
          options={RANGES}
        />
      </div>

      <Segments value={metric} options={METRICS} onChange={setMetric} />

      <Async state={report}>
        {(data) => (
          <>
            <div style={KPI_STRIP}>
              <KPI index={0} label="Total PRs" value={data.kpis.totalPullRequests} />
              <KPI
                index={1}
                label="Total reviews"
                value={data.kpis.totalReviews}
                delta={data.kpis.totalReviewsDelta}
              />
              <KPI
                index={2}
                label="Avg merge time"
                value={duration(data.kpis.avgMergeTimeMs)}
                delta={data.kpis.avgMergeTimeDelta}
              />
              <KPI
                index={3}
                label="# of bugs caught"
                value={data.kpis.bugsCaught}
                delta={data.kpis.bugsCaughtDelta}
              />
            </div>

            <PanelPair>
              <Panel
                style={column}
                title="PRs reviewed by CodeNerve"
                action={
                  <>
                    PRs reviewed <Chevron />
                  </>
                }
              >
                <LineChart series={data.series} valueKey="reviews" label="PRs reviewed" />
                <div style={SUBTITLE}>Top repos by review count</div>
                <Ranked rows={data.topRepositoriesByReviews} nameKey="repository" />
              </Panel>

              <Panel
                style={column}
                title="Bugs caught"
                action={
                  <>
                    All severity <Chevron />
                  </>
                }
              >
                <LineChart series={data.series} valueKey="findings" label="Bugs caught" />
                <div
                  style={{
                    ...SUBTITLE,
                    font: "10px/1.6 var(--font-mono)",
                    letterSpacing: "0.06em",
                  }}
                >
                  P0 {data.severity.p0} · P1 {data.severity.p1} · P2 {data.severity.p2}
                </div>
                <div style={SUBTITLE}>Repos with most bugs</div>
                <Ranked rows={data.topRepositoriesByFindings} nameKey="repository" />
              </Panel>
            </PanelPair>

            <PanelPair>
              <Panel
                style={column}
                title="Average time to merge"
                action={
                  <>
                    Mean <Chevron />
                  </>
                }
              >
                <div style={{ display: "flex", alignItems: "baseline", gap: "10px", padding: "16px 18px 0" }}>
                  <span style={BIG}>{duration(data.kpis.avgMergeTimeMs)}</span>
                </div>
                <div style={SUBTITLE}>Top repos by merge time</div>
                {/* No endpoint breaks merge time down per repository. */}
                <Empty>No data available</Empty>
              </Panel>

              <Panel style={column} title="Top contributors">
                <Ranked rows={data.topContributors} nameKey="author" />
              </Panel>
            </PanelPair>

            <PanelPair>
              <Panel style={column} title="✓ Addressed rate">
                <div style={{ display: "flex", alignItems: "baseline", gap: "10px", padding: "16px 18px 0" }}>
                  <span style={BIG}>
                    {data.addressedRate.percent === null ? "—" : `${data.addressedRate.percent}%`}
                  </span>
                  <span style={{ font: "var(--text-body-14)", color: "var(--text-faint)" }}>
                    {data.addressedRate.addressed} of{" "}
                    {data.addressedRate.addressed + data.addressedRate.unaddressed}
                  </span>
                </div>
                <LineChart series={data.series} valueKey="findings" label="Addressed rate" />
                <div style={SUBTITLE}>Top repos by addressed rate</div>
                <Ranked rows={data.topRepositoriesByFindings} nameKey="repository" />
              </Panel>

              <Panel style={column} title="Comment ratings">
                <div style={{ display: "flex", gap: "34px", padding: "16px 18px 0" }}>
                  <Count label="Upvotes" value={data.commentRatings.upvotes} />
                  <Count label="Downvotes" value={data.commentRatings.downvotes} />
                </div>
                <div style={SUBTITLE}>Most upvoted comments</div>
                {/* Votes are counted, but no endpoint ranks the comments that carry them. */}
                <Empty>No data available</Empty>
              </Panel>
            </PanelPair>
          </>
        )}
      </Async>
    </>
  );
}

function Count({ label, value }) {
  return (
    <span style={{ display: "grid", gap: "8px" }}>
      <span
        style={{
          font: "10px/1 var(--font-mono)",
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color: "var(--text-faint)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          font: "500 clamp(22px,2.2vw,30px)/1 var(--font-display)",
          letterSpacing: "-0.03em",
          color: "var(--text-primary)",
        }}
      >
        {value}
      </span>
    </span>
  );
}

/* A ranked list inside a panel: rows to the panel's own edges, or the dashed well when
   there is nothing to rank. */
function Ranked({ rows, nameKey }) {
  if (!rows?.length) return <Empty>No data available</Empty>;
  return (
    <ol style={{ listStyle: "none", margin: "10px 0 18px", padding: 0 }}>
      {rows.map((row, index) => (
        <li
          key={row[nameKey]}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            padding: "10px 18px",
            borderTop: index ? "1px solid var(--surface-elevated)" : "none",
            font: "var(--text-body-14)",
            color: "var(--text-secondary)",
          }}
        >
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
            {row[nameKey]}
          </span>
          <span style={{ color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
            {row.value}
          </span>
        </li>
      ))}
    </ol>
  );
}
